import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { ZONES } from "../lib/zones";
import { useTheme } from "../lib/theme";

export default function LocalPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { slug } = useParams();
  const zone = ZONES.find((z) => z.slug === slug);
  const lang = ["it", "en", "de"].includes(i18n.language) ? i18n.language : "it";
  const zt = zone?.i18n[lang];

  useEffect(() => {
    if (!zone) return;
    window.scrollTo(0, 0);
    document.title = t("local.title", { name: zone.name });
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t("local.meta", { name: zone.name }));
    return () => {
      document.title = t("local.defaultTitle");
    };
  }, [zone, t]);

  if (!zone || !zt) return <Navigate to="/" replace />;

  return (
    <div className={`bg-[#0D0F12]${theme === "light" ? " light" : ""}`} data-testid="local-page">
      <Nav />
      <main className="mx-auto max-w-7xl px-6 pb-28 pt-32 lg:px-12 lg:pt-40">
        <div className="grid items-end gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="flex items-center gap-2 font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]" data-testid="local-overline">
              <MapPin className="h-3.5 w-3.5" /> {zt.province}
            </p>
            <h1 className="mt-4 font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl" data-testid="local-heading">
              {t("local.h1pre")} <em className="italic text-[#D6705A]">{zone.name}</em>.
            </h1>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-[#A0A0A0] md:text-base" data-testid="local-copy">
              {zt.copy}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="overflow-hidden" data-testid="local-image-wrap">
              <img src={zone.img} alt={zt.alt} loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" data-testid="local-image" />
            </div>
            <p className="mt-4 font-code text-[10px] uppercase tracking-[0.3em] text-[#666]">{zt.tagline}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-wrap gap-2.5" data-testid="local-services">
            {zt.services.map((s) => (
              <span key={s} className="rounded-full border border-white/10 px-5 py-2.5 font-code text-[11px] tracking-[0.05em] text-[#C9C7C3]">
                {s}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-16 flex flex-col items-start justify-between gap-8 border border-[#D6705A]/30 bg-[#D6705A]/5 p-8 md:flex-row md:items-center md:p-10" data-testid="local-cta">
            <div>
              <h2 className="font-display text-2xl font-light text-[#F9F8F6] sm:text-3xl">{t("local.ctaTitle", { name: zone.name })}</h2>
              <p className="mt-2 max-w-md text-sm text-[#A0A0A0]">
                {t("local.ctaText")}
              </p>
            </div>
            <a
              href="/#prenota"
              className="group flex shrink-0 items-center gap-3 rounded-full bg-[#D6705A] px-8 py-4 font-code text-[11px] uppercase tracking-[0.25em] text-[#0D0F12] transition-[background-color,transform] duration-300 hover:bg-[#F9F8F6] hover:-translate-y-0.5"
              data-testid="local-cta-button"
            >
              {t("local.ctaBtn")} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 border-t border-white/5 pt-10">
            <p className="font-code text-[10px] uppercase tracking-[0.3em] text-[#666]">{t("local.otherZones")}</p>
            <div className="mt-5 flex flex-wrap items-baseline gap-x-8 gap-y-3" data-testid="local-other-zones">
              {ZONES.filter((z) => z.slug !== zone.slug).map((z) => (
                <Link
                  key={z.slug}
                  to={`/${z.slug}`}
                  className="font-display text-xl font-light text-[#A0A0A0] transition-colors duration-300 hover:text-[#D6705A]"
                  data-testid={`local-zone-link-${z.slug}`}
                >
                  {z.name}
                </Link>
              ))}
              <Link
                to="/"
                className="font-display text-xl font-light italic text-[#666] transition-colors duration-300 hover:text-[#D6705A]"
                data-testid="local-zone-link-home"
              >
                {t("local.allSicily")}
              </Link>
            </div>
          </div>
        </Reveal>
      </main>
      <Footer />
    </div>
  );
}
